import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Smartphone,
  MapPin,
  Battery,
  Clock,
  AlertTriangle,
  Activity,
  Circle,
  FileText,
  Upload,
  Loader2,
  KeyRound,
  RotateCcw,
  AlertOctagon,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { apiClient, UserDetail as UserDetailType, DriverDeadLetterStats } from '../api/client';
import { AppBadge } from '../components/AppFlavor';

export default function UserDetail() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const [user, setUser] = useState<UserDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestingLogs, setRequestingLogs] = useState(false);
  const [logRequestMessage, setLogRequestMessage] = useState<string | null>(null);
  const [deadLetterStats, setDeadLetterStats] = useState<DriverDeadLetterStats | null>(null);
  const [deadLetterLoading, setDeadLetterLoading] = useState(false);
  const [tokenRefreshing, setTokenRefreshing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [deadLetterMessage, setDeadLetterMessage] = useState<string | null>(null);

  const handleRequestLogs = async () => {
    if (!deviceId) return;
    setRequestingLogs(true);
    setLogRequestMessage(null);
    try {
      const result = await apiClient.requestLogs(deviceId);
      // Show message with error if present
      let message = result.message;
      if (result.pushError) {
        message += ` [Error: ${result.pushError}]`;
      }
      setLogRequestMessage(message);
    } catch (err) {
      setLogRequestMessage(err instanceof Error ? err.message : 'Failed to request logs');
    } finally {
      setRequestingLogs(false);
    }
  };

  const fetchDeadLetterStats = async () => {
    const driverId = user?.device?.driver_id;
    if (!driverId) return;
    setDeadLetterLoading(true);
    try {
      const stats = await apiClient.getDriverDeadLetterStats(driverId);
      setDeadLetterStats(stats);
    } catch {
      // Silently ignore - section just won't show
    } finally {
      setDeadLetterLoading(false);
    }
  };

  const handleTokenRefresh = async () => {
    const driverId = user?.device?.driver_id;
    if (!driverId) return;
    setTokenRefreshing(true);
    setDeadLetterMessage(null);
    try {
      const result = await apiClient.requestTokenRefresh(driverId);
      setDeadLetterMessage(result.message);
    } catch (err) {
      setDeadLetterMessage(err instanceof Error ? err.message : 'Failed');
    } finally {
      setTokenRefreshing(false);
    }
  };

  const handleRetryDeadLetter = async () => {
    const driverId = user?.device?.driver_id;
    if (!driverId) return;
    setRetrying(true);
    setDeadLetterMessage(null);
    try {
      const result = await apiClient.retryDriverDeadLetter(driverId);
      setDeadLetterMessage(
        `${result.message}${result.recovered ? ` (${result.recovered} recovered)` : ''}`
      );
      // Refresh stats after retry
      setTimeout(fetchDeadLetterStats, 2000);
    } catch (err) {
      setDeadLetterMessage(err instanceof Error ? err.message : 'Failed');
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    async function fetchUser() {
      if (!deviceId) return;
      try {
        const data = await apiClient.getUserDetail(deviceId);
        setUser(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user');
      } finally {
        setLoading(false);
      }
    }

    fetchUser();
  }, [deviceId]);

  // Fetch dead letter stats when user data is loaded
  useEffect(() => {
    setDeadLetterStats(null);
    setDeadLetterMessage(null);
    // Failed syncs are the Locate2u app's sync pipeline (CORE-8116); a FedEx device row must not
    // read or act on the same driver's Locate2u installation.
    if (user?.device?.driver_id && user.device.app_flavor !== 'fedex') {
      fetchDeadLetterStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.device?.driver_id, user?.device?.app_flavor]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'User not found'}</p>
        </div>
      </div>
    );
  }

  // Use is_online from API (consistent with user list)
  const isOnline = user.is_online;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/users"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to users
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                {user.device.email || user.device.driver_id || 'Not Logged In'}
                <AppBadge flavor={user.device.app_flavor} />
              </h1>
              {user.device.team_name && (
                <p className="text-sm text-gray-500">{user.device.team_name}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Circle
                size={10}
                className={isOnline ? 'text-green-500 fill-green-500' : 'text-gray-300 fill-gray-300'}
              />
              <span className="text-sm text-gray-600">
                {isOnline ? 'Online' : 'Offline'}
              </span>
              {user.is_moving && (
                <MapPin size={14} className="text-blue-500" aria-label="Moving" />
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRequestLogs}
              disabled={requestingLogs}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {requestingLogs ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Upload size={18} />
              )}
              Request Logs
            </button>
            {logRequestMessage && (
              <span className="text-sm text-gray-600 max-w-xs">{logRequestMessage}</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Device Info */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Smartphone size={20} />
            Device Info
          </h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs text-gray-500">Device ID</dt>
              <dd className="text-sm font-mono text-gray-900">{user.device.id}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Platform</dt>
              <dd className="text-sm text-gray-900">
                {user.device.platform === 'ios' ? 'iOS' : 'Android'} {user.device.os_version}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Model</dt>
              <dd className="text-sm text-gray-900">{user.device.device_model}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">App Version</dt>
              <dd className="text-sm text-gray-900">
                v{user.device.app_version}
                {user.device.build_number && <span className="text-gray-500"> ({user.device.build_number})</span>}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">First Seen</dt>
              <dd className="text-sm text-gray-900">
                {format(new Date(user.device.first_seen_at), 'PPp')}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Last Seen</dt>
              <dd className="text-sm text-gray-900">
                {formatDistanceToNow(new Date(user.device.last_seen_at), { addSuffix: true })}
              </dd>
            </div>
          </dl>
          {/* Diagnostics are keyed by the Locate2u user id (the token `sub`). FedEx rows link by it:
              their driver_id is filled only once the app has bridged its session (CORE-8116). */}
          {(user.device.app_flavor === 'fedex' ? user.device.user_id : user.device.driver_id) && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Link
                to={
                  user.device.app_flavor === 'fedex'
                    ? `/diagnostics?driver_id=${user.device.user_id}&app=fedex`
                    : `/diagnostics?driver_id=${user.device.driver_id}`
                }
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
              >
                <FileText size={16} />
                View Diagnostics Logs
              </Link>
            </div>
          )}
        </div>

        {/* Status */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity size={20} />
            Current Status
          </h2>
          <div className="space-y-4">
            {user.device.last_location_lat && user.device.last_location_lng && (
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  {user.device.last_location_lat.toFixed(4)}, {user.device.last_location_lng.toFixed(4)}
                </span>
              </div>
            )}
            {user.device.battery_level !== undefined && (
              <div className="flex items-center gap-2">
                <Battery size={16} className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  {Math.round(user.device.battery_level * 100)}% battery
                </span>
              </div>
            )}
            {user.current_session && (
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  Session started {formatDistanceToNow(new Date(user.current_session.started_at), { addSuffix: true })}
                </span>
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Today's Stats</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-2xl font-bold text-gray-900">
                  {user.stats.stops_completed_today}
                </span>
                <p className="text-xs text-gray-500">Stops completed</p>
              </div>
              <div>
                <span className="text-2xl font-bold text-gray-900">
                  {user.stats.total_sessions}
                </span>
                <p className="text-xs text-gray-500">Total sessions</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Crashes */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle size={20} />
            Recent Crashes
            {user.stats.total_crashes > 0 && (
              <span className="badge badge-error">{user.stats.total_crashes}</span>
            )}
          </h2>
          {user.recent_crashes.length === 0 ? (
            <p className="text-sm text-gray-500">No crashes recorded</p>
          ) : (
            <div className="space-y-3">
              {user.recent_crashes.slice(0, 5).map((crash) => (
                <Link
                  key={crash.id}
                  to={`/crashes/${crash.id}`}
                  className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {crash.error_message}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDistanceToNow(new Date(crash.occurred_at), { addSuffix: true })}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {user.device.app_flavor === 'fedex' && (
        <div className="card mt-6">
          <p className="text-sm text-gray-500">
            Failed Syncs apply to the Locate2u app only. The FedEx app does not use that sync
            pipeline.
          </p>
        </div>
      )}

      {user.device.app_flavor !== 'fedex' && (<>
      {/* Failed Syncs */}
      {deadLetterStats && deadLetterStats.totalItems > 0 && (
        <div className="card mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <AlertOctagon size={20} className="text-orange-500" />
              Failed Syncs
              <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded-full">
                {deadLetterStats.totalItems}
              </span>
            </h2>
            <div className="flex items-center gap-2">
              {deadLetterMessage && (
                <span className="text-xs text-gray-500">{deadLetterMessage}</span>
              )}
              <button
                onClick={handleTokenRefresh}
                disabled={tokenRefreshing || retrying}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 disabled:opacity-50 transition-colors"
              >
                {tokenRefreshing ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <KeyRound size={14} />
                )}
                Refresh Token
              </button>
              <button
                onClick={handleRetryDeadLetter}
                disabled={tokenRefreshing || retrying}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 disabled:opacity-50 transition-colors"
              >
                {retrying ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RotateCcw size={14} />
                )}
                Retry All
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stop ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {deadLetterStats.items.map((item) => (
                  <tr key={item.syncId}>
                    <td className="px-4 py-2">
                      <span className="badge badge-neutral text-xs">{item.type}</span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 font-mono">{item.stopId || '-'}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        item.reason === 'auth_expired' ? 'bg-orange-100 text-orange-800' :
                        item.reason === 'max_retries' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.reason}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate">{item.error}</td>
                    <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                      {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {deadLetterLoading && (
        <div className="card mt-6 text-center py-4">
          <Loader2 size={20} className="animate-spin mx-auto text-gray-400" />
          <p className="text-sm text-gray-500 mt-2">Loading failed syncs...</p>
        </div>
      )}
      </>)}

      {/* Recent Activity */}
      <div className="card mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        {user.recent_activity.length === 0 ? (
          <p className="text-sm text-gray-500">No recent activity</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Event
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Details
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {user.recent_activity.slice(0, 20).map((activity) => (
                  <tr key={activity.id}>
                    <td className="px-4 py-2">
                      <span className="badge badge-neutral">{activity.event_type}</span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {activity.event_data ? JSON.stringify(activity.event_data) : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {formatDistanceToNow(new Date(activity.occurred_at), { addSuffix: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Sessions */}
      <div className="card mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Session History</h2>
        {user.recent_sessions.length === 0 ? (
          <p className="text-sm text-gray-500">No sessions recorded</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Started
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Ended
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Duration
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {user.recent_sessions.map((session) => (
                  <tr key={session.id}>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      {format(new Date(session.started_at), 'PPp')}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {session.ended_at
                        ? format(new Date(session.ended_at), 'PPp')
                        : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {session.duration_seconds
                        ? `${Math.round(session.duration_seconds / 60)} min`
                        : '-'}
                    </td>
                    <td className="px-4 py-2">
                      {!session.ended_at ? (
                        <span className="badge badge-success">Active</span>
                      ) : (
                        <span className="badge badge-neutral">Ended</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
